import { useState } from 'react';
import { parseAsString, useQueryStates } from 'nuqs';
import { Plus } from 'lucide-react';
import { toast } from 'sonner';
import type { ExpenseDto } from '@barbeiropro/contracts';
import { ConfirmDialog } from '@/components/shared/confirm-dialog';
import { DataTable, type DataColumn } from '@/components/shared/data-table';
import { useTableParams } from '@/components/shared/use-table-params';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { errorMessage } from '@/lib/api';
import { fmtDate, money } from '@/lib/format';
import { useSession } from '@/lib/session';
import { useCancelExpense, useExpenses } from '../api';
import { EXPENSE_CATEGORIES } from '../labels';
import { Pill } from '../pill';
import { todayIn } from '../period';
import { ExpenseModal } from './expense-modal';

const ALL = 'all';

export function ExpensesTab({ from, to, branchId, tz }: { from: string; to: string; branchId: string | null; tz: string }) {
  const { can } = useSession();
  const params = useTableParams({ sort: 'dueDate', order: 'desc' });
  const [filters, setFilters] = useQueryStates({ categoria: parseAsString, situacao: parseAsString }, { history: 'replace' });
  const q = useExpenses({ ...params.query, from, to, branchId, category: filters.categoria, status: filters.situacao });
  const [editing, setEditing] = useState<ExpenseDto | 'new' | null>(null);
  const cancel = useCancelExpense();
  const today = todayIn(tz);
  const manage = can('expenses.manage');

  const columns: DataColumn<ExpenseDto>[] = [
    { header: 'Vencimento', accessorFn: (r) => r.dueDate, cell: ({ row }) => <span className="tabular">{fmtDate(row.original.dueDate, 'UTC')}</span>, meta: { sortKey: 'dueDate' } },
    { header: 'Categoria', accessorKey: 'category', meta: { sortKey: 'category' } },
    { header: 'Descrição', accessorKey: 'description', meta: { className: 'max-w-64 truncate' } },
    { header: 'Valor', accessorFn: (r) => r.amountCents, cell: ({ row }) => money(row.original.amountCents), meta: { sortKey: 'amountCents', align: 'right' } },
    {
      header: 'Situação',
      id: 'status',
      cell: ({ row }) => {
        const e = row.original;
        if (e.cancelledAt) return <Pill>Cancelada</Pill>;
        if (e.paidAt) return <Pill tone="good">Paga em {fmtDate(e.paidAt.slice(0, 10), 'UTC')}</Pill>;
        return e.dueDate.slice(0, 10) < today ? <Pill tone="bad">Vencida</Pill> : <Pill tone="warn">A pagar</Pill>;
      },
    },
    {
      id: 'actions',
      header: () => <span className="sr-only">Ações</span>,
      cell: ({ row }) =>
        manage && !row.original.cancelledAt ? (
          <div className="flex justify-end gap-1">
            <Button size="sm" variant="outline" onClick={() => setEditing(row.original)}>
              Editar
            </Button>
            <ConfirmDialog
              trigger={<Button size="sm" variant="ghost">Cancelar</Button>}
              title="Cancelar esta despesa?"
              description={`A despesa "${row.original.description}" deixa de contar nos relatórios. Essa ação não pode ser desfeita.`}
              confirmLabel="Cancelar despesa"
              onConfirm={async () => {
                try {
                  await cancel.mutateAsync(row.original.id);
                  toast.success('Despesa cancelada.');
                } catch (e) {
                  toast.error(errorMessage(e));
                }
              }}
            />
          </div>
        ) : null,
    },
  ];

  return (
    <>
      <DataTable
        columns={columns}
        data={q.data}
        params={params}
        isLoading={q.isLoading}
        error={q.error}
        onRetry={() => q.refetch()}
        searchPlaceholder="Buscar por descrição"
        empty={{
          title: 'Nenhuma despesa no período',
          description: 'Lance aluguel, energia, produtos e outras contas para ver o resultado real.',
          action: manage ? (
            <Button onClick={() => setEditing('new')}>
              <Plus className="size-4" aria-hidden /> Nova despesa
            </Button>
          ) : undefined,
        }}
        filters={
          <>
            <Select value={filters.categoria ?? ALL} onValueChange={(v) => { void setFilters({ categoria: v === ALL ? null : v }); void params.set({ page: 1 }); }}>
              <SelectTrigger aria-label="Categoria" className="w-44">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>Todas as categorias</SelectItem>
                {EXPENSE_CATEGORIES.map((c) => (
                  <SelectItem key={c} value={c}>
                    {c}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={filters.situacao ?? ALL} onValueChange={(v) => { void setFilters({ situacao: v === ALL ? null : v }); void params.set({ page: 1 }); }}>
              <SelectTrigger aria-label="Situação" className="w-40">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>Todas (sem canceladas)</SelectItem>
                <SelectItem value="paid">Pagas</SelectItem>
                <SelectItem value="unpaid">A pagar</SelectItem>
                <SelectItem value="cancelled">Canceladas</SelectItem>
              </SelectContent>
            </Select>
          </>
        }
        toolbar={
          manage && (
            <Button onClick={() => setEditing('new')}>
              <Plus className="size-4" aria-hidden /> Nova despesa
            </Button>
          )
        }
      />
      {editing && <ExpenseModal expense={editing} defaultBranchId={branchId} today={today} onClose={() => setEditing(null)} />}
    </>
  );
}
